import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useDropzone, FileRejection } from "react-dropzone";
import { toast } from "react-toastify";
import {
  pickupSelector,
  uploadBol
} from "../../../slices/pickup/pickupSlice";
import XGSPopup, { PopupTheme } from "../../../ui-components/xgs-popup/xgsPopup";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import Loading from "../../../ui-components/loading/loading";
import { fileToBase64 } from "../../../services/common/file";
import uploadImg from "../../../images/upload_gray.svg";
import uploadSmallImg from "../../../images/upload_white.svg";
import "./bolRecognize.scss";

interface BolUploadProps {
  show: boolean;
  onClose: () => void;
  onSuccess: (bolData?: any, file?: File) => void;
};

const UPLOAD_MAX_SIZE = 4; // MB

const UPLOAD_FILE_TYPES = {
  "application/pdf": [],
  "image/jpeg": [],
  "image/png": [],
  "image/bmp": [],
  "image/tiff": [],
  "image/heif": [],
  "image/heic": []
};

const BolUpload: React.FC<BolUploadProps> = ({ onSuccess, ...props }) => {
  const dispatch = useDispatch();
  const pickupState = useSelector(pickupSelector);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");

  const {
    getRootProps,
    getInputProps
  } = useDropzone({
    accept: UPLOAD_FILE_TYPES,
    maxSize: UPLOAD_MAX_SIZE * 1048576,
    maxFiles: 1,
    onDrop: (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setFileError("");
      if (fileRejections?.length > 0) {
        fileRejections[0].errors.forEach((err) => {
          if (err.code === "file-too-large") {
            setFileError(`A file no larger than ${UPLOAD_MAX_SIZE} MB is allowed!`);
          }
          if (err.code === "file-invalid-type") {
            setFileError("Only a file of certain formats is allowed!");
          }
          if (err.code === "too-many-files") {
            setFileError("Only one file is allowed!");
          }
        });
      }
      if (acceptedFiles.length === 0) return;
      // setFiles([...acceptedFiles]);

      const file = acceptedFiles[0];
      setFiles([file]);
      setUploadedFile(file);

      const mimeType = file.type;
      let fileType = mimeType.split('/').pop()?.toUpperCase();
      if (fileType === "HEIC") fileType = "HEIF";


      fileToBase64(file)
        .then((result) => {
          const request = {
            fileType: fileType || "",
            documentData: result
          };

          dispatch(uploadBol(request, URL.createObjectURL(file), () => { }, (err: any) => {
            toast.error(err, { autoClose: 4000 });
            setFiles([]);
          }));
        }
        )
        .catch((err) => {
          toast.error(err, { autoClose: 4000 });
        });
    }
  });

  useEffect(() => {
    if (pickupState.requestSucceed && pickupState.requestCreator === "UPLOAD_BOL" && uploadedFile) {
      onSuccess(null, uploadedFile);
      setUploadedFile(null); // reset local file after passing
    }
  }, [pickupState.requestSucceed, pickupState.requestCreator, uploadedFile, onSuccess]);

  return (
    <XGSPopup
      opened={props.show}
      onClose={() => props.onClose()}
      className="xgs-bol-recognize__modal xgs-modal"
      closeOnDocumentClick={false}
      closeOnEscape={false}
      theme={PopupTheme.NEW}
      size="561px"
    >
      <div className="xgs-bol-recognize">
        <div className="xgs-bol-recognize__header">
          Upload BOL documents for recognition
        </div>
        <div className="xgs-bol-recognize__text">
          Upload a BOL document in one of the supported formats and the system will try to recognize it. After that, you can check the recognition result.
        </div>
        {(files.length === 0) && (
          <div {...getRootProps({ className: "xgs-upload__area" })}>
            <input {...getInputProps()} />
            <div className="xgs-upload__area__image">
              <img src={uploadImg} alt="upload" />
            </div>
            Drag and drop files to upload or click the button
            <div className="xgs-bol-recognize__button">
              <Button
                theme={ButtonThemes.blue}
                onClick={() => { }}
              >
                <img src={uploadSmallImg} alt="Upload" />
                Upload File
              </Button>
            </div>
            <div className="xgs-upload__notes">
              Only <strong>JPG</strong>, <strong>PNG</strong>, <strong>BMP</strong>, <strong>TIFF</strong>, <strong>HEIF</strong> and <strong>PDF</strong> files are supported.
            </div>
            {fileError && (
              <span className="xgs-upload__area__error">
                {fileError}
              </span>
            )}
          </div>
        )}
        {pickupState.requestStarted && pickupState.requestCreator === "UPLOAD_BOL" && (
          <div className="xgs-bol-recognize__spinner">
            <Loading isLoading />
          </div>
        )}
      </div>
    </XGSPopup>
  );
};

export default BolUpload;